// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { startOfMonth, endOfMonth, subMonths, addMonths, format, parseISO, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import TransactionForm from '../components/TransactionForm';
import { buildCategoryOptions, inferCategory } from '../utils/categories';

import {
    Container, Typography, Box, Grid, Card, CardContent, Fab, Modal,
    IconButton, AppBar, Toolbar, CircularProgress, Chip, Divider, Tooltip, Avatar, LinearProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Logout as LogoutIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    AccountBalance as AccountBalanceIcon,
    Savings as SavingsIcon,
    ArrowBack as ArrowBackIcon,
    CreditCard as CreditCardIcon,
    Repeat as RepeatIcon,
    ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';

const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const TYPE_CONFIG = {
    income: { label: 'Entrada', color: 'success', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    fixedExpense: { label: 'Fixa', color: 'warning', icon: <RepeatIcon sx={{ fontSize: 14 }} /> },
    variableExpense: { label: 'Variável', color: 'error', icon: <ShoppingCartIcon sx={{ fontSize: 14 }} /> },
    installment: { label: 'Parcelado', color: 'info', icon: <CreditCardIcon sx={{ fontSize: 14 }} /> },
};

const Dashboard = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const { month: monthParam } = useParams();

    const [currentMonth, setCurrentMonth] = useState(() => {
        if (monthParam) {
            try { return parse(monthParam, 'yyyy-MM', new Date()); } catch { /* ignore */ }
        }
        const savedMonth = localStorage.getItem('currentMonth');
        return savedMonth ? parseISO(savedMonth) : new Date();
    });

    const [transactions, setTransactions] = useState([]);
    const [previousMonthBalance, setPreviousMonthBalance] = useState(0);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        localStorage.setItem('currentMonth', currentMonth.toISOString());
    }, [currentMonth]);

    const fetchData = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);

        const endOfPrevMonth = endOfMonth(subMonths(currentMonth, 1));
        const prevBalanceQuery = query(
            collection(db, 'transactions'),
            where('userId', '==', currentUser.uid),
            where('date', '<=', endOfPrevMonth)
        );
        const prevSnapshot = await getDocs(prevBalanceQuery);
        let balance = 0;
        prevSnapshot.forEach(doc => {
            const t = doc.data();
            balance += t.type === 'income' ? t.amount : -t.amount;
        });
        setPreviousMonthBalance(balance);

        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const currentMonthQuery = query(
            collection(db, 'transactions'),
            where('userId', '==', currentUser.uid),
            where('date', '>=', start),
            where('date', '<=', end),
            orderBy('date', 'asc')
        );
        const currentSnapshot = await getDocs(currentMonthQuery);
        const monthlyTransactions = currentSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            date: doc.data().date.toDate(),
            category: doc.data().category || inferCategory(doc.data()),
        }));
        setTransactions(monthlyTransactions);

        const allTransactionsQuery = query(
            collection(db, 'transactions'),
            where('userId', '==', currentUser.uid)
        );
        const allSnapshot = await getDocs(allTransactionsQuery);
        const allTransactions = allSnapshot.docs.map(doc => doc.data());
        setCategoryOptions(buildCategoryOptions(allTransactions));
        setLoading(false);
    }, [currentUser, currentMonth]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDelete = async (transactionToDelete) => {
        const isConfirmed = window.confirm(`Tem certeza que deseja excluir "${transactionToDelete.description}"?`);
        if (!isConfirmed) return;
        try {
            if (transactionToDelete.isInstallment) {
                const batch = writeBatch(db);
                const installmentId = transactionToDelete.installmentDetails.installmentId;
                const q = query(
                    collection(db, 'transactions'),
                    where('userId', '==', currentUser.uid),
                    where('installmentDetails.installmentId', '==', installmentId),
                    where('date', '>=', transactionToDelete.date)
                );
                const querySnapshot = await getDocs(q);
                querySnapshot.forEach(document => batch.delete(document.ref));
                await batch.commit();
            } else if (transactionToDelete.isRecurring) {
                const batch = writeBatch(db);
                const q = query(
                    collection(db, 'transactions'),
                    where('userId', '==', currentUser.uid),
                    where('recurringId', '==', transactionToDelete.recurringId),
                    where('date', '>=', transactionToDelete.date)
                );
                const querySnapshot = await getDocs(q);
                querySnapshot.forEach(document => batch.delete(document.ref));
                await batch.commit();
            } else {
                await deleteDoc(doc(db, 'transactions', transactionToDelete.id));
            }
            fetchData();
        } catch (error) {
            console.error('Erro ao deletar:', error);
            alert('Ocorreu um erro ao tentar excluir o lançamento.');
        }
    };

    const summary = useMemo(() => {
        const income = transactions.filter(t => t.type === 'income').reduce((acc, t) => acc + t.amount, 0);
        const expenses = transactions.filter(t => t.type !== 'income').reduce((acc, t) => acc + t.amount, 0);
        return { income, expenses, finalBalance: previousMonthBalance + income - expenses };
    }, [transactions, previousMonthBalance]);

    const categorySummary = useMemo(() => {
        const totals = {};

        transactions.forEach(t => {
            const category = t.category || inferCategory(t);
            if (!totals[category]) {
                totals[category] = { category, income: 0, expenses: 0, count: 0 };
            }

            totals[category].count += 1;
            if (t.type === 'income') {
                totals[category].income += t.amount;
            } else {
                totals[category].expenses += t.amount;
            }
        });

        return Object.values(totals)
            .map(item => ({
                ...item,
                balance: item.income - item.expenses,
                total: item.income + item.expenses,
            }))
            .sort((a, b) => b.total - a.total);
    }, [transactions]);

    const maxCategoryTotal = useMemo(() => (
        Math.max(...categorySummary.map(item => item.total), 1)
    ), [categorySummary]);

    useEffect(() => {
        if (
            selectedCategory !== 'all' &&
            !categorySummary.some(item => item.category === selectedCategory)
        ) {
            setSelectedCategory('all');
        }
    }, [categorySummary, selectedCategory]);

    const filteredTransactions = useMemo(() => {
        if (selectedCategory === 'all') return transactions;
        return transactions.filter(t => (t.category || inferCategory(t)) === selectedCategory);
    }, [selectedCategory, transactions]);

    const handleLogout = async () => { await logout(); navigate('/login'); };
    const changeMonth = (amount) => setCurrentMonth(prev => addMonths(prev, amount));
    const userInitial = currentUser?.email?.[0]?.toUpperCase() || 'U';
    const handleOpenNewTransaction = () => {
        setEditingTransaction(null);
        setOpenModal(true);
    };
    const handleOpenEditTransaction = (transaction) => {
        setEditingTransaction(transaction);
        setOpenModal(true);
    };
    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingTransaction(null);
    };

    const groupedTransactions = useMemo(() => {
        const groups = {};
        filteredTransactions.forEach(t => {
            const key = format(t.date, 'yyyy-MM-dd');
            if (!groups[key]) groups[key] = [];
            groups[key].push(t);
        });
        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    }, [filteredTransactions]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            {/* AppBar */}
            <AppBar position="sticky">
                <Toolbar>
                    <Tooltip title="Voltar ao início">
                        <IconButton color="inherit" onClick={() => navigate('/')} sx={{ mr: 1 }} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight={700}>
                            Minha Contabilidade
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary', fontSize: '0.875rem' }}>
                            {currentUser?.email}
                        </Typography>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700 }}>
                            {userInitial}
                        </Avatar>
                        <Tooltip title="Sair">
                            <IconButton color="inherit" onClick={handleLogout} size="small">
                                <LogoutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4, pb: 12 }}>
                {/* Month Navigator */}
                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    mb: 4, gap: 2
                }}>
                    <IconButton onClick={() => changeMonth(-1)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={800} sx={{ textTransform: 'capitalize', lineHeight: 1.1 }}>
                            {format(currentMonth, 'MMMM', { locale: ptBR })}
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            {format(currentMonth, 'yyyy')}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => changeMonth(1)}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
                        <ChevronRightIcon />
                    </IconButton>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <Box className="fade-in">
                        {/* Summary Cards */}
                        <Grid container spacing={2.5} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Saldo Anterior"
                                    value={previousMonthBalance}
                                    icon={<SavingsIcon />}
                                    color="#6366f1"
                                    gradient="linear-gradient(135deg, rgba(99,102,241,0.15), rgba(99,102,241,0.05))"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Entradas do Mês"
                                    value={summary.income}
                                    icon={<TrendingUpIcon />}
                                    color="#10b981"
                                    gradient="linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Despesas do Mês"
                                    value={summary.expenses}
                                    icon={<TrendingDownIcon />}
                                    color="#f43f5e"
                                    gradient="linear-gradient(135deg, rgba(244,63,94,0.15), rgba(244,63,94,0.05))"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryCard
                                    title="Saldo Final"
                                    value={summary.finalBalance}
                                    icon={<AccountBalanceIcon />}
                                    color={summary.finalBalance >= 0 ? '#6366f1' : '#f43f5e'}
                                    gradient={summary.finalBalance >= 0
                                        ? "linear-gradient(135deg, rgba(99,102,241,0.2), rgba(99,102,241,0.05))"
                                        : "linear-gradient(135deg, rgba(244,63,94,0.2), rgba(244,63,94,0.05))"}
                                    isBold
                                />
                            </Grid>
                        </Grid>

                        {categorySummary.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 1.5,
                                    mb: 2
                                }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>Valores por Categoria</Typography>
                                        <Typography color="text.secondary" variant="body2">
                                            Toque em uma categoria para filtrar os lançamentos do mês
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label="Todas"
                                        color={selectedCategory === 'all' ? 'primary' : 'default'}
                                        variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
                                        onClick={() => setSelectedCategory('all')}
                                        sx={{ fontWeight: 700 }}
                                    />
                                </Box>

                                <Grid container spacing={1.5}>
                                    {categorySummary.map(item => {
                                        const isSelected = selectedCategory === item.category;
                                        const isPositive = item.balance >= 0;

                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={item.category}>
                                                <Card
                                                    onClick={() => setSelectedCategory(isSelected ? 'all' : item.category)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        height: '100%',
                                                        borderColor: isSelected ? 'primary.main' : 'rgba(148, 163, 184, 0.08)',
                                                        boxShadow: isSelected
                                                            ? '0 0 0 1px rgba(99,102,241,0.45), 0 8px 32px rgba(0,0,0,0.4)'
                                                            : undefined,
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }}>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                                                                    {item.category}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.count} lançamento{item.count !== 1 ? 's' : ''}
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                fontWeight={800}
                                                                color={isPositive ? 'success.main' : 'error.main'}
                                                                sx={{ whiteSpace: 'nowrap' }}
                                                            >
                                                                {formatCurrency(item.balance)}
                                                            </Typography>
                                                        </Box>

                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(item.total / maxCategoryTotal) * 100}
                                                            sx={{
                                                                height: 6,
                                                                mb: 1.25,
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: isPositive ? '#10b981' : '#f43f5e'
                                                                }
                                                            }}
                                                        />

                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                            <Typography variant="caption" color="success.main">
                                                                + {formatCurrency(item.income)}
                                                            </Typography>
                                                            <Typography variant="caption" color="error.main">
                                                                - {formatCurrency(item.expenses)}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                        {/* Transactions */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            mb: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="h6" fontWeight={700}>Lançamentos do Mês</Typography>
                                {selectedCategory !== 'all' && (
                                    <Chip
                                        label={selectedCategory}
                                        size="small"
                                        color="primary"
                                        onDelete={() => setSelectedCategory('all')}
                                    />
                                )}
                            </Box>
                            <Typography color="text.secondary" variant="body2">
                                {filteredTransactions.length} de {transactions.length} lançamento{transactions.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>

                        {groupedTransactions.length > 0 ? (
                            <Box>
                                {groupedTransactions.map(([dateKey, dayTransactions]) => (
                                    <Box key={dateKey} sx={{ mb: 3 }}>
                                        {/* Day header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                            <Box sx={{
                                                px: 1.5, py: 0.5, borderRadius: '6px',
                                                backgroundColor: 'rgba(99,102,241,0.15)',
                                                border: '1px solid rgba(99,102,241,0.2)'
                                            }}>
                                                <Typography variant="caption" color="primary.light" fontWeight={600}>
                                                    {format(dayTransactions[0].date, "dd 'de' MMMM", { locale: ptBR })}
                                                </Typography>
                                            </Box>
                                            <Divider sx={{ flex: 1 }} />
                                        </Box>

                                        {/* Day transactions */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {dayTransactions.map(t => {
                                                const typeConf = TYPE_CONFIG[t.type] || TYPE_CONFIG.variableExpense;
                                                const isIncome = t.type === 'income';
                                                return (
                                                    <Card key={t.id} sx={{
                                                        '&:hover': { transform: 'translateY(-1px)' },
                                                        borderLeft: `3px solid ${isIncome ? '#10b981' : '#f43f5e'}`
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                                            flexDirection: { xs: 'column', sm: 'row' },
                                                            px: { xs: 2, sm: 2.5 },
                                                            py: 1.5,
                                                            gap: { xs: 1.25, sm: 2 }
                                                        }}>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight={600}
                                                                        sx={{ overflowWrap: 'anywhere' }}
                                                                    >
                                                                        {t.description}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={typeConf.label}
                                                                        size="small"
                                                                        color={typeConf.color}
                                                                        icon={typeConf.icon}
                                                                        variant="outlined"
                                                                        sx={{ height: 20 }}
                                                                    />
                                                                    <Chip
                                                                        label={t.category || inferCategory(t)}
                                                                        size="small"
                                                                        variant="filled"
                                                                        sx={{
                                                                            height: 20,
                                                                            backgroundColor: 'rgba(148,163,184,0.12)',
                                                                            color: 'text.secondary'
                                                                        }}
                                                                    />
                                                                </Box>
                                                                {t.cardName && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Cartão: {t.cardName}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 1,
                                                                flexShrink: 0,
                                                                width: { xs: '100%', sm: 'auto' }
                                                            }}>
                                                                <Typography
                                                                    fontWeight={700}
                                                                    color={isIncome ? 'success.main' : 'error.main'}
                                                                    sx={{ whiteSpace: 'nowrap' }}
                                                                >
                                                                    {isIncome ? '+' : '-'} {formatCurrency(t.amount)}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <Tooltip title="Editar">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleOpenEditTransaction(t)}
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                '&:hover': { color: 'primary.light', backgroundColor: 'rgba(99,102,241,0.1)' }
                                                                            }}
                                                                        >
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Excluir">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDelete(t)}
                                                                            sx={{
                                                                                color: 'text.secondary',
                                                                                '&:hover': { color: 'error.main', backgroundColor: 'rgba(244,63,94,0.1)' }
                                                                            }}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    </Card>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Card sx={{ textAlign: 'center', py: 6 }}>
                                <CardContent>
                                    <Typography color="text.secondary" variant="h6" gutterBottom>
                                        Nenhum lançamento neste mês
                                    </Typography>
                                    <Typography color="text.secondary" variant="body2">
                                        Clique no botão + para adicionar um lançamento
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}

                {/* FAB */}
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1050 }}
                    onClick={handleOpenNewTransaction}
                >
                    <AddIcon />
                </Fab>

                {/* Modal */}
                <Modal
                    open={openModal}
                    onClose={handleCloseModal}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
                >
                    <Box sx={{ width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto' }}>
                        <TransactionForm
                            onTransactionAdded={fetchData}
                            onClose={handleCloseModal}
                            transactionToEdit={editingTransaction}
                            categoryOptions={categoryOptions}
                            defaultDate={currentMonth}
                        />
                    </Box>
                </Modal>
            </Container>
        </Box>
    );
};

const SummaryCard = ({ title, value, icon, color, gradient, isBold }) => (
    <Card sx={{ background: gradient, border: `1px solid ${color}22`, height: '100%' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>{title}</Typography>
                    <Typography variant="h6" fontWeight={isBold ? 800 : 700} color={color}>
                        {formatCurrency(value)}
                    </Typography>
                </Box>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '10px',
                    backgroundColor: `${color}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: color
                }}>
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default Dashboard;
